import { useState } from 'react';
import { Alert, Button, Snackbar, Stack } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOnOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { exportFinanceReport, FinanceExportParams } from '../../api/finance';
import { useAuth } from '../../context/authContext';
import { downloadBlob } from '../../utils/downloadBlob';

/** Excel/PDF export of a finance list with its current filters. Hidden
 * without payments:export (the server refuses it too). */
export const ExportButtons = ({ params }: { params: Omit<FinanceExportParams, 'format'> }) => {
  const { hasPermission } = useAuth();
  const [busy, setBusy] = useState<'xlsx' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!hasPermission('payments', 'export')) return null;

  const run = async (format: 'xlsx' | 'pdf') => {
    setBusy(format);
    try {
      const { blob, filename } = await exportFinanceReport({ ...params, format });
      downloadBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't export the report");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<GridOnIcon />}
        disabled={!!busy}
        onClick={() => run('xlsx')}
      >
        {busy === 'xlsx' ? 'Exporting…' : 'Excel'}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PictureAsPdfIcon />}
        disabled={!!busy}
        onClick={() => run('pdf')}
      >
        {busy === 'pdf' ? 'Exporting…' : 'PDF'}
      </Button>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
